/**
 * View-mode override (OPUS-1086) — lets privileged users (super user / admin /
 * tester) switch the AeDrive view between "drive" and "copilot" from the footer
 * Settings toggle. The choice is persisted in localStorage so a reload restores
 * the same view, and they can switch back at any time.
 *
 * IMPORTANT: this only changes the VIEW. The session's real `role` is left
 * untouched, so event telemetry (`user_role`) keeps reporting the actual role.
 * Only the view-determining gates read `isCopilotView()`.
 */
const VIEW_OVERRIDE_KEY = "aedrive_view_override";
const VALID_VIEWS = new Set(["copilot", "drive"]);

// Roles permitted to switch views. Case-insensitive. Adjust here if the auth
// layer uses different role strings for these privileged users.
const VIEW_SWITCH_ROLES = new Set([
  "super_admin",
  "super_user",
  "superuser",
  "super",
  "admin",
  "tester",
]);

/** True if this session's role may switch views (super user / admin / tester). */
export const canSwitchView = (session) => {
  const role = String(session?.role || "").trim().toLowerCase();
  return VIEW_SWITCH_ROLES.has(role);
};

/** The persisted view override ('copilot' | 'drive'), or null if none set. */
export const getViewOverride = () => {
  try {
    const v = localStorage.getItem(VIEW_OVERRIDE_KEY);
    return VALID_VIEWS.has(v) ? v : null;
  } catch {
    return null;
  }
};

/** Persist (or clear, with null) the view override. */
export const setViewOverride = (view) => {
  try {
    if (VALID_VIEWS.has(view)) localStorage.setItem(VIEW_OVERRIDE_KEY, view);
    else localStorage.removeItem(VIEW_OVERRIDE_KEY);
  } catch {
    // localStorage unavailable (private mode / quota) — non-fatal.
  }
};

/**
 * Effective view role: the override (only when the user is allowed to switch),
 * then the app the session is assigned to (a Copilot session opens the copilot
 * view for every visitor, e.g. a super admin), otherwise the session's real
 * role. Non-privileged users never get a stored override.
 */
export const resolveViewRole = (session) => {
  if (canSwitchView(session)) {
    const override = getViewOverride();
    if (override) return override;
  }
  if (session?.applicationFlavour === "copilot") return "copilot";
  return session?.role || null;
};

/** Convenience: is the EFFECTIVE view the copilot view? */
export const isCopilotView = (session) => resolveViewRole(session) === "copilot";
